import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getBooksByLibrary,
  getBookCopiesByBookId,
  addBookCopy,
  getErrorMessage
} from '../../api/api';
import '../BookList.css';
import '../lib-page-header.css';

const AddBookCopy = () => {
  const navigate = useNavigate();
  const sectionBase = useSectionBase();
  const [searchParams] = useSearchParams();

  const libraryId = useActiveLibraryId();
  const queryBookId = searchParams.get('book_id');

  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState([]);

  const [formData, setFormData] = useState({
    book_id: queryBookId || '',
    accession_no: '',
    barcode: '',
    catalogue_no: '',
    copy_number: 1,

    room_no: '',
    almirah_no: '',
    rack_no: '',
    shelf_no: '',
    row_no: '',

    purchase_date: '',
    purchase_price: '',
    vendor_name: '',

    condition_status: 'good',
    availability_status: 'available',
    is_reference_only: 0,
    is_discarded: 0,
    discarded_at: '',
    discard_reason: '',
    remarks: ''
  });

  const fetchBooks = async () => {
    try {
      if (!libraryId) return;

      const data = await getBooksByLibrary(libraryId);
      setBooks(data || []);
    } catch (error) {
      console.error('Fetch books error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const setNextCopyNumber = async (bookId) => {
    try {
      if (!bookId) return;

      const copies = await getBookCopiesByBookId(bookId);
      const nextCopyNumber = (copies?.length || 0) + 1;

      setFormData((prev) => ({
        ...prev,
        copy_number: nextCopyNumber
      }));
    } catch (error) {
      console.error('Copy number error:', error);
    }
  };

  useEffect(() => {
    fetchBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  useEffect(() => {
    if (queryBookId) {
      setNextCopyNumber(queryBookId);
    }
  }, [queryBookId]);

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));

    if (name === 'book_id' && value) {
      await setNextCopyNumber(value);
    }
  };

  const validateForm = () => {
    if (!formData.book_id) {
      Swal.fire('Error', 'Please select book', 'error');
      return false;
    }

    if (!formData.accession_no.trim()) {
      Swal.fire('Error', 'Accession no is required', 'error');
      return false;
    }

    if (!formData.barcode.trim()) {
      Swal.fire('Error', 'Barcode is required', 'error');
      return false;
    }

    if (!formData.copy_number) {
      Swal.fire('Error', 'Copy number is required', 'error');
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setFormData({
      book_id: queryBookId || '',
      accession_no: '',
      barcode: '',
      catalogue_no: '',
      copy_number: 1,

      room_no: '',
      almirah_no: '',
      rack_no: '',
      shelf_no: '',
      row_no: '',

      purchase_date: '',
      purchase_price: '',
      vendor_name: '',

      condition_status: 'good',
      availability_status: 'available',
      is_reference_only: 0,
      is_discarded: 0,
      discarded_at: '',
      discard_reason: '',
      remarks: ''
    });

    if (queryBookId) {
      setNextCopyNumber(queryBookId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      book_id: formData.book_id,
      accession_no: formData.accession_no.trim(),
      barcode: formData.barcode.trim(),
      catalogue_no: formData.catalogue_no || null,
      copy_number: formData.copy_number,

      room_no: formData.room_no || null,
      almirah_no: formData.almirah_no || null,
      rack_no: formData.rack_no || null,
      shelf_no: formData.shelf_no || null,
      row_no: formData.row_no || null,

      purchase_date: formData.purchase_date || null,
      purchase_price: formData.purchase_price || null,
      vendor_name: formData.vendor_name || null,

      condition_status: formData.condition_status || 'good',
      availability_status: formData.availability_status || 'available',
      is_reference_only: formData.is_reference_only ? 1 : 0,
      is_discarded: formData.is_discarded ? 1 : 0,
      discarded_at: formData.discarded_at || null,
      discard_reason: formData.discard_reason || null,
      remarks: formData.remarks || null
    };

    try {
      setLoading(true);

      await addBookCopy(payload);

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Book copy added successfully'
      });

      resetForm();

      if (queryBookId) {
        navigate(`${sectionBase}/book-copies?book_id=${queryBookId}`);
      } else {
        navigate(`${sectionBase}/book-copies`);
      }
    } catch (error) {
      console.error('Add book copy error:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: getErrorMessage(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedBook = books.find(
    (book) => String(book.id) === String(formData.book_id)
  );

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-copy" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Add Book Copy</h3>
            <div className="lib-page-header__subtitle">Add physical copy, barcode and location details</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button
            className="btn btn-secondary"
            onClick={() =>
              queryBookId
                ? navigate(`${sectionBase}/book-copies?book_id=${queryBookId}`)
                : navigate(`${sectionBase}/book-copies`)
            }
          >
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm mb-3">
          <div className="card-header fw-bold"><i className="fas fa-book me-2" />Book Selection</div>

          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Book *</label>
                <SearchableSelect
                  name="book_id"
                  value={formData.book_id}
                  onChange={handleChange}
                  options={books.map((book) => ({
                    value: book.id,
                    label: `${book.title} ${book.author_name ? `- ${book.author_name}` : ''}`
                  }))}
                  placeholder="Select Book"
                  disabled={Boolean(queryBookId)}
                  required
                  clearable={false}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Selected Book</label>
                <input
                  type="text"
                  className="form-control"
                  value={
                    selectedBook
                      ? `${selectedBook.title || ''} ${
                          selectedBook.isbn ? `(${selectedBook.isbn})` : ''
                        }`
                      : ''
                  }
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow-sm mb-3">
          <div className="card-header fw-bold"><i className="fas fa-layer-group me-2" />Copy Details</div>

          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Accession No *</label>
                <input
                  type="text"
                  name="accession_no"
                  className="form-control"
                  placeholder="Example: ACC-001"
                  value={formData.accession_no}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Barcode *</label>
                <input
                  type="text"
                  name="barcode"
                  className="form-control"
                  placeholder="Example: BAR-001"
                  value={formData.barcode}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Catalogue No</label>
                <input
                  type="text"
                  name="catalogue_no"
                  className="form-control"
                  value={formData.catalogue_no}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Copy Number *</label>
                <input
                  type="number"
                  name="copy_number"
                  className="form-control"
                  min="1"
                  value={formData.copy_number}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Room No</label>
                <input
                  type="text"
                  name="room_no"
                  className="form-control"
                  value={formData.room_no}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Almirah No</label>
                <input
                  type="text"
                  name="almirah_no"
                  className="form-control"
                  value={formData.almirah_no}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Rack No</label>
                <input
                  type="text"
                  name="rack_no"
                  className="form-control"
                  value={formData.rack_no}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Shelf No</label>
                <input
                  type="text"
                  name="shelf_no"
                  className="form-control"
                  value={formData.shelf_no}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Row No</label>
                <input
                  type="text"
                  name="row_no"
                  className="form-control"
                  value={formData.row_no}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Purchase Date</label>
                <input
                  type="date"
                  name="purchase_date"
                  className="form-control"
                  value={formData.purchase_date}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Purchase Price</label>
                <input
                  type="number"
                  name="purchase_price"
                  className="form-control"
                  value={formData.purchase_price}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Vendor Name</label>
                <input
                  type="text"
                  name="vendor_name"
                  className="form-control"
                  value={formData.vendor_name}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Condition</label>
                <SearchableSelect
                  name="condition_status"
                  value={formData.condition_status}
                  onChange={handleChange}
                  options={[
                    { value: 'good', label: 'Good' },
                    { value: 'damaged', label: 'Damaged' },
                    { value: 'lost', label: 'Lost' }
                  ]}
                  clearable={false}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Availability</label>
                <SearchableSelect
                  name="availability_status"
                  value={formData.availability_status}
                  onChange={handleChange}
                  options={[
                    { value: 'available', label: 'Available' },
                    { value: 'reserved', label: 'Reserved' },
                    { value: 'issued', label: 'Issued' },
                    { value: 'damaged', label: 'Damaged' },
                    { value: 'lost', label: 'Lost' },
                    { value: 'discarded', label: 'Discarded' }
                  ]}
                  clearable={false}
                />
              </div>

              <div className="col-md-3 mb-3 d-flex align-items-end">
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="is_reference_only"
                    className="form-check-input"
                    id="isReferenceOnly"
                    checked={Boolean(formData.is_reference_only)}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="isReferenceOnly">
                    Reference Only
                  </label>
                </div>
              </div>

              <div className="col-md-3 mb-3 d-flex align-items-end">
                <div className="form-check">
                  <input
                    type="checkbox"
                    name="is_discarded"
                    className="form-check-input"
                    id="isDiscarded"
                    checked={Boolean(formData.is_discarded)}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="isDiscarded">
                    Discarded
                  </label>
                </div>
              </div>

              {Boolean(formData.is_discarded) && (
                <>
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Discarded At</label>
                    <input
                      type="date"
                      name="discarded_at"
                      className="form-control"
                      value={formData.discarded_at}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-9 mb-3">
                    <label className="form-label">Discard Reason</label>
                    <input
                      type="text"
                      name="discard_reason"
                      className="form-control"
                      value={formData.discard_reason}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}

              <div className="col-md-12 mb-3">
                <label className="form-label">Remarks</label>
                <textarea
                  name="remarks"
                  rows="3"
                  className="form-control"
                  value={formData.remarks}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="text-end mb-4">
          <button
            type="button"
            className="btn btn-light me-2"
            onClick={() =>
              queryBookId
                ? navigate(`${sectionBase}/book-copies?book_id=${queryBookId}`)
                : navigate(`${sectionBase}/book-copies`)
            }
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Copy'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBookCopy;